import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import axiosInstance from '../../../api/axiosInstance';
import AlertModal from '../../../reusable/AlertModal';
import imageCompression from 'browser-image-compression';

const placeholderImageBase64 = "data:image/gif;base64,R0lGODlhAQABAIAAAMLCwgAAACH5BAAAAAAALAAAAAABAAEAAAICRAEAOw==";

const locationData = [
  { state: "Karnataka", cities: ["Bangalore", "Mysore", "Hubli-Dharwad", "Mangalore", "Belgaum", "Gulbarga", "Davanagere", "Bellary", "Bijapur", "Shimoga", "Tumkur", "Raichur", "Bidar", "Hassan", "Udupi", "Chikmagalur", "Karwar", "Kolar", "Chitradurga", "Haveri", "Mandya", "Gadag", "Bagalkot", "Koppal", "Yadgir", "Chamrajnagar", "Ramanagara", "Chikkaballapur", "Madikeri"] },
  { state: "Andaman and Nicobar Islands", cities: ["Port Blair", "Garacharma", "Bambooflat"] },
  { state: "Andhra Pradesh", cities: ["Visakhapatnam", "Vijayawada", "Guntur", "Nellore", "Kurnool"] },
  { state: "Arunachal Pradesh", cities: ["Itanagar", "Tawang", "Naharlagun", "Pasighat"] },
  { state: "Assam", cities: ["Guwahati", "Silchar", "Dibrugarh", "Jorhat", "Nagaon"] },
  { state: "Bihar", cities: ["Patna", "Gaya", "Bhagalpur", "Muzaffarpur", "Purnia"] },
  { state: "Chandigarh", cities: ["Chandigarh"] },
  { state: "Chhattisgarh", cities: ["Raipur", "Bhilai", "Bilaspur", "Korba", "Durg"] },
  { state: "Dadra and Nagar Haveli and Daman and Diu", cities: ["Daman", "Diu", "Silvassa"] },
  { state: "Delhi", cities: ["New Delhi", "North Delhi", "South Delhi", "West Delhi", "East Delhi"] },
  { state: "Goa", cities: ["Panaji", "Margao", "Vasco da Gama", "Mapusa", "Ponda"] },
  { state: "Gujarat", cities: ["Ahmedabad", "Surat", "Vadodara", "Rajkot", "Bhavnagar"] },
  { state: "Haryana", cities: ["Gurugram", "Faridabad", "Panipat", "Ambala", "Rohtak"] },
  { state: "Himachal Pradesh", cities: ["Shimla", "Manali", "Dharamshala", "Mandi", "Solan"] },
  { state: "Jammu and Kashmir", cities: ["Srinagar", "Jammu", "Anantnag", "Baramulla", "Kathua"] },
  { state: "Jharkhand", cities: ["Ranchi", "Jamshedpur", "Dhanbad", "Bokaro", "Deoghar"] },
  { state: "Kerala", cities: ["Thiruvananthapuram", "Kochi", "Kozhikode", "Thrissur", "Kollam"] },
  { state: "Ladakh", cities: ["Leh", "Kargil"] },
  { state: "Lakshadweep", cities: ["Kavaratti", "Agatti", "Minicoy"] },
  { state: "Madhya Pradesh", cities: ["Indore", "Bhopal", "Jabalpur", "Gwalior", "Ujjain"] },
  { state: "Maharashtra", cities: ["Mumbai", "Pune", "Nagpur", "Nashik", "Kolhapur", "Aurangabad", "Solapur"] },
  { state: "Manipur", cities: ["Imphal", "Thoubal", "Bishnupur", "Churachandpur"] },
  { state: "Meghalaya", cities: ["Shillong", "Tura", "Nongstoin", "Jowai"] },
  { state: "Mizoram", cities: ["Aizawl", "Lunglei", "Saiha", "Champhai"] },
  { state: "Nagaland", cities: ["Kohima", "Dimapur", "Mokokchung", "Tuensang"] },
  { state: "Odisha", cities: ["Bhubaneswar", "Cuttack", "Rourkela", "Berhampur", "Puri"] },
  { state: "Puducherry", cities: ["Puducherry", "Ozhukarai", "Karaikal", "Yanam", "Mahe"] },
  { state: "Punjab", cities: ["Ludhiana", "Amritsar", "Jalandhar", "Patiala", "Bathinda"] },
  { state: "Rajasthan", cities: ["Jaipur", "Jodhpur", "Udaipur", "Kota", "Bikaner"] },
  { state: "Sikkim", cities: ["Gangtok", "Namchi", "Gyalshing", "Mangan"] },
  { state: "Tamil Nadu", cities: ["Chennai", "Coimbatore", "Madurai", "Tiruchirappalli", "Salem"] },
  { state: "Telangana", cities: ["Hyderabad", "Warangal", "Nizamabad", "Karimnagar", "Khammam"] },
  { state: "Tripura", cities: ["Agartala", "Dharmanagar", "Udaipur", "Kailashahar"] },
  { state: "Uttar Pradesh", cities: ["Lucknow", "Kanpur", "Agra", "Varanasi", "Noida"] },
  { state: "Uttarakhand", cities: ["Dehradun", "Haridwar", "Roorkee", "Nainital", "Rishikesh"] },
  { state: "West Bengal", cities: ["Kolkata", "Howrah", "Darjeeling", "Siliguri", "Asansol"] }
];

// SearchableSelect component
const SearchableSelect = ({ name, value, options, onChange, placeholder, disabled, error }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const wrapperRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm("");
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredOptions = options.filter(o => o.toLowerCase().includes(searchTerm.toLowerCase()));

  return (
    <div className="relative" ref={wrapperRef}>
      <div className="relative">
        <input
          type="text" disabled={disabled}
          value={isOpen ? searchTerm : value}
          onChange={(e) => { setSearchTerm(e.target.value); setIsOpen(true); }}
          onClick={() => setIsOpen(true)}
          placeholder={value || placeholder}
          className={`w-full px-4 py-3 pr-10 rounded-xl border outline-none font-medium transition-colors focus:ring-2 focus:ring-indigo-500
            ${error ? 'border-red-400 bg-red-50 dark:bg-red-900/20 dark:border-red-700' : 'border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700'}
            text-gray-900 dark:text-slate-100 placeholder:text-gray-400 dark:placeholder:text-slate-500
            disabled:bg-gray-100 dark:disabled:bg-slate-700/50 disabled:text-gray-400 dark:disabled:text-slate-500 disabled:cursor-not-allowed`}
        />
        <div className="absolute inset-y-0 right-0 flex items-center px-4 pointer-events-none text-gray-500 dark:text-slate-400">
          <svg className={`w-4 h-4 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M19 9l-7 7-7-7"></path>
          </svg>
        </div>
      </div>
      {isOpen && !disabled && (
        <ul className="absolute z-[9999] w-full mt-1 bg-white dark:bg-slate-800 border border-gray-100 dark:border-slate-600 rounded-xl shadow-[0_10px_40px_-10px_rgba(0,0,0,0.2)] max-h-60 overflow-y-auto animate-in fade-in zoom-in-95 duration-100 no-scrollbar">
          {filteredOptions.length > 0 ? filteredOptions.map((option) => (
            <li key={option} onClick={() => { onChange({ target: { name, value: option } }); setSearchTerm(""); setIsOpen(false); }}
              className={`px-4 py-2.5 cursor-pointer text-sm font-semibold transition-colors ${value === option ? 'bg-indigo-50 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-400' : 'text-gray-700 dark:text-slate-300 hover:bg-indigo-50 dark:hover:bg-slate-700 hover:text-indigo-700 dark:hover:text-indigo-400'}`}>
              {option}
            </li>
          )) : (
            <li className="px-4 py-3 text-sm font-medium text-gray-500 dark:text-slate-400 text-center">No results found</li>
          )}
        </ul>
      )}
    </div>
  );
};

// Main AdminProfile component
const AdminProfile = () => {
  const [loading, setLoading]                         = useState(true);
  const [saving, setSaving]                           = useState(false);
  const [uploadingPhoto, setUploadingPhoto]           = useState(false);
  const [updatingPassword, setUpdatingPassword]       = useState(false);
  const [showPassword, setShowPassword]               = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [successMessage, setSuccessMessage]           = useState("");
  const [globalAlert, setGlobalAlert]                 = useState({ isOpen: false, title: "", message: "" });
  const [errors, setErrors]                           = useState({});
  const [passwordErrors, setPasswordErrors]           = useState({});

  const fileInputRef                          = useRef(null);
  const [selectedFileBlob, setSelectedFileBlob] = useState(null);
  const [previewImage, setPreviewImage]         = useState(null);
  const [initialData, setInitialData]           = useState(null);

  const [formData, setFormData] = useState({
    id: "", name: "", email: "", phone: "",
    address: "", address2: "", city: "", state: "", pincode: "", profile: null,
  });

  const [passwordData, setPasswordData] = useState({ password: "", confirmPassword: "" });

  // Fetch profile
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const res = await axiosInstance.get('/profile/get');
        if (res.data) {
          const p = res.data;
          const userId = p.id || "";
          const imageUrl = userId ? `${axiosInstance.defaults.baseURL}/profile/get-image/${userId}?t=${Date.now()}` : null;
          const fetched = {
            id: userId, name: p.name || "", email: p.email || "", phone: p.phone || "",
            address: p.address || "", address2: p.address2 || "", city: p.city || "",
            state: p.state || "", pincode: p.pincode || "", profile: imageUrl,
          };
          setFormData(fetched);
          setInitialData(fetched);
        }
      } catch {
        setGlobalAlert({ isOpen: true, title: "Connection Error", message: "Failed to load profile data." });
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, []);

  // Dirty check
  const isDirty = JSON.stringify({
    name: formData.name, email: formData.email, address: formData.address,
    address2: formData.address2, city: formData.city, state: formData.state, pincode: formData.pincode,
  }) !== JSON.stringify({
    name: initialData?.name, email: initialData?.email, address: initialData?.address,
    address2: initialData?.address2, city: initialData?.city, state: initialData?.state, pincode: initialData?.pincode,
  });

  // Validation
  const validateField = (name, value, currentDataObj = formData) => {
    if (name !== 'address2' && value.trim() === "") return "";
    let err = "";
    switch (name) {
      case "name":
        if (!/^[a-zA-Z\s]+$/.test(value)) err = "Name cannot contain numbers or special characters.";
        else if (value.trim().length <= 3) err = "Name must be more than 3 characters long.";
        break;
      case "email":
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) err = "Enter a valid email address.";
        break;
      case "address":
        if (value.trim().length < 5) err = "Please enter a complete address.";
        break;
      case "state":
        if (!locationData.some(item => item.state.toLowerCase() === value.toLowerCase()))
          err = "Please select a valid state from the list.";
        break;
      case "city":
        if (!locationData.some(item => item.cities.some(c => c.toLowerCase() === value.toLowerCase())))
          err = "Please select a valid city from the list.";
        break;
      case "pincode":
        if (!/^\d{6}$/.test(value)) err = "PIN code must be exactly 6 digits.";
        break;
      case "password":
        if (!/^(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/.test(value))
          err = "Must be 8+ chars with 1 uppercase, 1 number, and 1 symbol.";
        if (currentDataObj.confirmPassword && value !== currentDataObj.confirmPassword)
          setPasswordErrors(prev => ({ ...prev, confirmPassword: "Passwords do not match." }));
        else if (currentDataObj.confirmPassword && value === currentDataObj.confirmPassword)
          setPasswordErrors(prev => ({ ...prev, confirmPassword: "" }));
        break;
      case "confirmPassword":
        if (value !== currentDataObj.password) err = "Passwords do not match.";
        break;
      default: break;
    }
    return err;
  };

  const validateProfileForm = () => {
    const newErrors = {};
    let isValid = true;
    ['name', 'email', 'address', 'city', 'state', 'pincode'].forEach(field => {
      if (formData[field].trim() === "") { newErrors[field] = "This field is required."; isValid = false; }
      else { const e = validateField(field, formData[field]); if (e) { newErrors[field] = e; isValid = false; } }
    });
    setErrors(newErrors);
    return isValid;
  };

  const validatePasswordForm = () => {
    const newErrors = {};
    let isValid = true;
    if (!passwordData.password.trim()) { newErrors.password = "New password is required."; isValid = false; }
    else { const e = validateField("password", passwordData.password, passwordData); if (e) { newErrors.password = e; isValid = false; } }
    if (!passwordData.confirmPassword.trim()) { newErrors.confirmPassword = "Confirm password is required."; isValid = false; }
    else { const e = validateField("confirmPassword", passwordData.confirmPassword, passwordData); if (e) { newErrors.confirmPassword = e; isValid = false; } }
    setPasswordErrors(newErrors);
    return isValid;
  };

  // Handlers
  const handleProfileChange = (e) => {
    const { name, value } = e.target;
    const finalValue = name === 'pincode' ? value.replace(/\D/g, '') : value;
    setFormData(prev => { const u = { ...prev, [name]: finalValue }; if (name === "state") u.city = ""; return u; });
    const err = validateField(name, finalValue);
    setErrors(prev => ({ ...prev, [name]: err || "" }));
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({ ...prev, [name]: value }));
    const newData = { ...passwordData, [name]: value };
    const err = validateField(name, value, newData);
    setPasswordErrors(prev => ({ ...prev, [name]: err || "" }));
  };

  const availableCities = locationData.find(s => s.state === formData.state)?.cities || [];

  // Photo actions
  const handleImageSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 2097152) { setGlobalAlert({ isOpen: true, title: "File Too Large", message: "Profile picture must be smaller than 2MB." }); return; }
    try {
      const compressed = await imageCompression(file, { maxSizeMB: 0.5, maxWidthOrHeight: 512, useWebWorker: true, fileType: 'image/jpeg' });
      setSelectedFileBlob(compressed);
      setPreviewImage(URL.createObjectURL(compressed));
    } catch { setGlobalAlert({ isOpen: true, title: "Processing Failed", message: "Failed to optimize the image file." }); }
  };

  const handleUploadPhoto = async () => {
    if (!selectedFileBlob) return;
    setUploadingPhoto(true);
    try {
      const fd = new FormData();
      fd.append('profile', selectedFileBlob);
      await axiosInstance.put('/profile/set-photo', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const newUrl = `${axiosInstance.defaults.baseURL}/profile/get-image/${formData.id}?t=${Date.now()}`;
      setFormData(prev => ({ ...prev, profile: newUrl }));
      setInitialData(prev => ({ ...prev, profile: newUrl }));
      setPreviewImage(null); setSelectedFileBlob(null);
      setSuccessMessage("Profile photo uploaded successfully!"); setTimeout(() => setSuccessMessage(""), 3000);
    } catch (error) { setGlobalAlert({ isOpen: true, title: "Upload Failed", message: error.response?.data?.message || "Failed to upload photo." }); }
    finally { setUploadingPhoto(false); }
  };

  const handleRemovePhoto = async () => {
    try {
      await axiosInstance.delete('/profile/remove-photo');
      setFormData(prev => ({ ...prev, profile: placeholderImageBase64 }));
      setInitialData(prev => ({ ...prev, profile: placeholderImageBase64 }));
      setPreviewImage(null); setSelectedFileBlob(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      setSuccessMessage("Profile photo removed successfully."); setTimeout(() => setSuccessMessage(""), 3000);
    } catch { setGlobalAlert({ isOpen: true, title: "Action Failed", message: "Failed to remove photo." }); }
  };

  // Form submissions
  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    if (!isDirty) return;
    if (!validateProfileForm()) { setGlobalAlert({ isOpen: true, title: "Validation Error", message: "Please fix the errors in the form before saving." }); return; }
    setSaving(true);
    try {
      await axiosInstance.post('/profile/set', {
        name: formData.name, email: formData.email, address: formData.address,
        address2: formData.address2, city: formData.city, state: formData.state, pincode: formData.pincode,
      });
      setSuccessMessage("Profile details updated successfully!"); setInitialData(formData); setTimeout(() => setSuccessMessage(""), 3000);
    } catch (err) { setGlobalAlert({ isOpen: true, title: "Update Failed", message: err.response?.data?.message || "Failed to update profile details." }); }
    finally { setSaving(false); }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    if (!validatePasswordForm()) { setGlobalAlert({ isOpen: true, title: "Validation Error", message: "Please fix the password errors before saving." }); return; }
    setUpdatingPassword(true);
    try {
      await axiosInstance.post('/profile/update-password', { password: passwordData.password });
      setSuccessMessage("Password updated securely!"); setPasswordData({ password: "", confirmPassword: "" }); setPasswordErrors({}); setTimeout(() => setSuccessMessage(""), 3000);
    } catch (err) { setGlobalAlert({ isOpen: true, title: "Update Failed", message: err.response?.data?.message || "Failed to change password." }); }
    finally { setUpdatingPassword(false); }
  };

  const displayImage = previewImage || formData.profile;

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div></div>;
  }

  return (
    <div className="w-full px-3 sm:px-6 lg:px-24 space-y-6 pb-10">

      {successMessage && (
        <div className="fixed top-24 right-6 z-50 bg-white dark:bg-slate-800 border border-green-200 dark:border-green-700 shadow-[0_10px_40px_-10px_rgba(0,0,0,0.15)] rounded-2xl p-4 flex items-start gap-4 animate-in slide-in-from-right-8 fade-in duration-300">
          <div className="w-8 h-8 rounded-full bg-green-100 dark:bg-green-900/40 flex items-center justify-center flex-shrink-0">
            <svg className="w-5 h-5 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7"></path></svg>
          </div>
          <div className="mr-4 mt-1">
            <h4 className="text-sm font-bold text-gray-900 dark:text-slate-100 leading-none mb-1">Success</h4>
            <p className="text-xs font-semibold text-gray-500 dark:text-slate-400">{successMessage}</p>
          </div>
          <button onClick={() => setSuccessMessage("")} className="text-gray-400 dark:text-slate-500 hover:text-gray-700 dark:hover:text-slate-300 transition-colors p-1">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
          </button>
        </div>
      )}

      {/* Breadcrumb */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4">
        <nav className="flex" aria-label="Breadcrumb">
          <ol className="inline-flex items-center space-x-1 md:space-x-3">
            <li className="inline-flex items-center">
              <Link to="/admin/dashboard" className="inline-flex items-center text-sm font-bold text-slate-500 dark:text-slate-400 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors">
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"></path></svg>
                Dashboard
              </Link>
            </li>
            <li>
              <div className="flex items-center">
                <svg className="w-5 h-5 text-gray-400 dark:text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7"></path></svg>
                <span className="ml-1 text-sm font-bold text-gray-900 dark:text-slate-200 md:ml-2">Admin Profile</span>
              </div>
            </li>
          </ol>
        </nav>
        <div className="inline-flex items-center px-4 py-2 rounded-full bg-gradient-to-r from-indigo-100 to-indigo-50 dark:from-indigo-900/30 dark:to-indigo-800/20 border border-indigo-200 dark:border-indigo-700/50 shadow-sm w-fit">
          <span className="w-2 h-2 rounded-full bg-indigo-500 mr-2 animate-pulse"></span>
          <span className="text-sm font-bold text-indigo-700 dark:text-indigo-400 uppercase tracking-wider">Administrator</span>
        </div>
      </div>

      {/* Personal Details Card */}
      <div className="bg-white dark:bg-slate-800 rounded-3xl shadow-sm border border-gray-100 dark:border-slate-700">
        <div className="p-6 sm:p-10">
          <h2 className="text-xl font-bold text-gray-900 dark:text-slate-100 mb-8 border-b border-gray-100 dark:border-slate-700 pb-4">Personal Details</h2>
          <form onSubmit={handleProfileSubmit}>

            {/* Profile picture */}
            <div className="flex flex-col sm:flex-row items-center gap-8 mb-10">
              <div className="relative group cursor-pointer" onClick={() => fileInputRef.current.click()} title="Select a new photo">
                <div className="w-36 h-36 rounded-full bg-indigo-50 dark:bg-slate-700 border-4 border-white dark:border-white shadow-xl overflow-hidden flex items-center justify-center relative transition-transform duration-300 group-hover:scale-[1.02]">
                  <img src={displayImage} alt="Profile" className="w-full h-full object-cover"
                    onError={(e) => { e.target.onerror = null; e.target.src = placeholderImageBase64; }} />
                  <div className="absolute inset-0 bg-indigo-900/60 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                    <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z"></path></svg>
                  </div>
                </div>
                <input type="file" ref={fileInputRef} onChange={handleImageSelect} accept="image/jpeg, image/png, image/webp" className="hidden" />
              </div>
              <div className="text-center sm:text-left flex flex-col items-center sm:items-start">
                <h3 className="text-lg font-bold text-gray-900 dark:text-slate-100">Profile Photo</h3>
                <p className="text-sm text-gray-500 dark:text-slate-400 mt-1 mb-4 max-w-xs leading-relaxed">JPEG, PNG, or WEBP up to 2MB. Click the image to select a file.</p>
                <div className="flex flex-wrap gap-3 justify-center sm:justify-start">
                  <button type="button" disabled={!selectedFileBlob || uploadingPhoto} onClick={handleUploadPhoto}
                    className="flex items-center gap-2 px-5 py-2.5 rounded-xl font-bold text-sm text-white shadow-md transition-all disabled:bg-gray-300 dark:disabled:bg-slate-600 disabled:shadow-none bg-indigo-600 hover:bg-indigo-700 active:scale-95">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"></path></svg>
                    {uploadingPhoto ? "Uploading..." : "Upload Photo"}
                  </button>
                  <button type="button" disabled={(formData.profile === placeholderImageBase64) && !previewImage} onClick={handleRemovePhoto}
                    className="flex items-center gap-2 px-5 py-2.5 rounded-xl font-bold text-sm text-red-600 dark:text-red-400 border-2 border-red-100 dark:border-red-800/50 hover:bg-red-50 dark:hover:bg-red-900/20 transition-all disabled:opacity-50 disabled:cursor-not-allowed active:scale-95">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path></svg>
                    Remove
                  </button>
                </div>
              </div>
            </div>

            {/* Form fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-6">
              <div className="lg:col-span-1">
                <label className="block text-sm font-semibold text-gray-700 dark:text-slate-300 mb-1.5">Full Name</label>
                <input type="text" name="name" value={formData.name} onChange={handleProfileChange}
                  className={`w-full px-4 py-3 rounded-xl border ${errors.name ? 'border-red-400 bg-red-50 dark:bg-red-900/20' : 'border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700'} focus:ring-2 focus:ring-indigo-500 outline-none font-medium text-gray-900 dark:text-slate-100 transition-colors`} />
                {errors.name && <p className="text-red-500 text-xs mt-1.5 font-medium">{errors.name}</p>}
              </div>
              <div className="lg:col-span-1">
                <label className="block text-sm font-semibold text-gray-700 dark:text-slate-300 mb-1.5">Email Address</label>
                <input type="email" name="email" value={formData.email} onChange={handleProfileChange}
                  className={`w-full px-4 py-3 rounded-xl border ${errors.email ? 'border-red-400 bg-red-50 dark:bg-red-900/20' : 'border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700'} focus:ring-2 focus:ring-indigo-500 outline-none font-medium text-gray-900 dark:text-slate-100 transition-colors`} />
                {errors.email && <p className="text-red-500 text-xs mt-1.5 font-medium">{errors.email}</p>}
              </div>
              <div className="lg:col-span-1">
                <label className="block text-sm font-semibold text-gray-700 dark:text-slate-300 mb-1.5">Phone Number</label>
                <input type="text" value={`+91 ${formData.phone}`} readOnly
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-slate-600 bg-gray-100 dark:bg-slate-700/50 text-gray-500 dark:text-slate-400 cursor-not-allowed outline-none font-medium" />
              </div>
              <div className="md:col-span-2 lg:col-span-3 grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-slate-300 mb-1.5">Address Line 1</label>
                  <input type="text" name="address" value={formData.address} onChange={handleProfileChange} placeholder="Street address or P.O. Box"
                    className={`w-full px-4 py-3 rounded-xl border ${errors.address ? 'border-red-400 bg-red-50 dark:bg-red-900/20' : 'border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700'} focus:ring-2 focus:ring-indigo-500 outline-none font-medium text-gray-900 dark:text-slate-100 placeholder:text-gray-400 dark:placeholder:text-slate-500 transition-colors`} />
                  {errors.address && <p className="text-red-500 text-xs mt-1.5 font-medium">{errors.address}</p>}
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 dark:text-slate-300 mb-1.5">Address Line 2 <span className="text-gray-400 dark:text-slate-500 font-normal">(Optional)</span></label>
                  <input type="text" name="address2" value={formData.address2} onChange={handleProfileChange} placeholder="Apt, suite, unit, building, etc."
                    className="w-full px-4 py-3 rounded-xl border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700 focus:ring-2 focus:ring-indigo-500 outline-none font-medium text-gray-900 dark:text-slate-100 placeholder:text-gray-400 dark:placeholder:text-slate-500 transition-colors" />
                </div>
              </div>
              <div className="lg:col-span-1">
                <label className="block text-sm font-semibold text-gray-700 dark:text-slate-300 mb-1.5">State</label>
                <SearchableSelect name="state" value={formData.state} options={locationData.map(i => i.state)} onChange={handleProfileChange} placeholder="Type to search state..." error={errors.state} />
                {errors.state && <p className="text-red-500 text-xs mt-1.5 font-medium">{errors.state}</p>}
              </div>
              <div className="lg:col-span-1">
                <label className="block text-sm font-semibold text-gray-700 dark:text-slate-300 mb-1.5">City</label>
                <SearchableSelect name="city" value={formData.city} options={availableCities} onChange={handleProfileChange} placeholder="Type to search city..." disabled={!formData.state} error={errors.city} />
                {errors.city && <p className="text-red-500 text-xs mt-1.5 font-medium">{errors.city}</p>}
              </div>
              <div className="lg:col-span-1">
                <label className="block text-sm font-semibold text-gray-700 dark:text-slate-300 mb-1.5">PIN Code</label>
                <input type="text" name="pincode" value={formData.pincode} onChange={handleProfileChange} maxLength="6"
                  className={`w-full px-4 py-3 rounded-xl border ${errors.pincode ? 'border-red-400 bg-red-50 dark:bg-red-900/20' : 'border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700'} focus:ring-2 focus:ring-indigo-500 outline-none font-medium text-gray-900 dark:text-slate-100 transition-colors`} />
                {errors.pincode && <p className="text-red-500 text-xs mt-1.5 font-medium">{errors.pincode}</p>}
              </div>
            </div>

            <div className="mt-10 flex justify-end border-t border-gray-100 dark:border-slate-700 pt-8">
              <button type="submit" disabled={!isDirty || saving}
                className={`px-10 py-3.5 rounded-xl font-bold text-white shadow-lg transition-all duration-300 ${!isDirty ? 'bg-gray-300 dark:bg-slate-600 cursor-not-allowed shadow-none' : saving ? 'bg-indigo-400 cursor-not-allowed' : 'bg-indigo-600 hover:bg-indigo-700 active:scale-[0.98] hover:shadow-indigo-200'}`}>
                {!isDirty ? "No Changes to Save" : saving ? "Saving Profile..." : "Update Details"}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Security Card */}
      <div className="bg-white dark:bg-slate-800 rounded-3xl shadow-sm border border-gray-100 dark:border-slate-700 overflow-hidden mt-8">
        <div className="p-6 sm:p-10">
          <h2 className="text-xl font-bold text-gray-900 dark:text-slate-100 mb-8 border-b border-gray-100 dark:border-slate-700 pb-4">Security Settings</h2>
          <form onSubmit={handlePasswordSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-6 items-end">

              <div className="lg:col-span-1">
                <label className="block text-sm font-semibold text-gray-700 dark:text-slate-300 mb-1.5">New Password</label>
                <div className="relative">
                  <input type={showPassword ? "text" : "password"} name="password" value={passwordData.password} onChange={handlePasswordChange} placeholder="Enter new password"
                    className={`w-full px-4 py-3 pr-12 rounded-xl border ${passwordErrors.password ? 'border-red-400 bg-red-50 dark:bg-red-900/20' : 'border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700'} focus:ring-2 focus:ring-indigo-500 outline-none font-medium text-gray-900 dark:text-slate-100 placeholder:text-gray-400 dark:placeholder:text-slate-500 transition-colors`} />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 dark:text-slate-500 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors focus:outline-none">
                    {showPassword
                      ? <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/></svg>
                      : <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"/></svg>
                    }
                  </button>
                </div>
                {passwordErrors.password && <p className="text-red-500 text-xs mt-1.5 font-medium absolute">{passwordErrors.password}</p>}
              </div>

              <div className="lg:col-span-1">
                <label className="block text-sm font-semibold text-gray-700 dark:text-slate-300 mb-1.5">Confirm New Password</label>
                <div className="relative">
                  <input type={showConfirmPassword ? "text" : "password"} name="confirmPassword" value={passwordData.confirmPassword} onChange={handlePasswordChange} placeholder="Confirm new password"
                    className={`w-full px-4 py-3 pr-12 rounded-xl border ${passwordErrors.confirmPassword ? 'border-red-400 bg-red-50 dark:bg-red-900/20' : 'border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-700'} focus:ring-2 focus:ring-indigo-500 outline-none font-medium text-gray-900 dark:text-slate-100 placeholder:text-gray-400 dark:placeholder:text-slate-500 transition-colors`} />
                  <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 dark:text-slate-500 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors focus:outline-none">
                    {showConfirmPassword
                      ? <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/></svg>
                      : <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"/></svg>
                    }
                  </button>
                </div>
                {passwordErrors.confirmPassword && <p className="text-red-500 text-xs mt-1.5 font-medium absolute">{passwordErrors.confirmPassword}</p>}
              </div>

              <div className="lg:col-span-1">
                <button type="submit" disabled={updatingPassword || !passwordData.password || !passwordData.confirmPassword}
                  className={`w-full py-3.5 rounded-xl font-bold text-white shadow-lg transition-all duration-300 ${(!passwordData.password || !passwordData.confirmPassword) ? 'bg-gray-300 dark:bg-slate-600 cursor-not-allowed shadow-none' : updatingPassword ? 'bg-indigo-400 cursor-not-allowed' : 'bg-gray-900 dark:bg-slate-200 dark:text-slate-900 hover:bg-gray-800 dark:hover:bg-white active:scale-[0.98]'}`}>
                  {updatingPassword ? "Updating..." : "Update Password"}
                </button>
              </div>

            </div>
          </form>
        </div>
      </div>

      <AlertModal isOpen={globalAlert.isOpen} title={globalAlert.title} message={globalAlert.message} onClose={() => setGlobalAlert({ ...globalAlert, isOpen: false })} />
    </div>
  );
};

export default AdminProfile;